// Auth Hook - Clean wrapper around auth store
import { useAuthStore } from '@stores/authStore';
import type { AuthUser } from '@lib/auth/types';
import type { Session } from '@supabase/supabase-js';

export interface UseAuthReturn {
  user: AuthUser | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pendingMfaEmail: string | null;
  initialize: () => Promise<void>;
  signUp: (email: string, password: string, username?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  completeMfaLogin: (email: string, code: string) => Promise<void>;
  requestMfaCodeAgain: () => Promise<void>;
  clearPendingMfa: () => void;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  fetchUserProfile: () => Promise<void>;
  updateUser: (updates: Partial<AuthUser>) => Promise<void>;
  clearError: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const store = useAuthStore();

  return {
    // State
    user: store.user,
    session: store.session,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,
    pendingMfaEmail: store.pendingMfaEmail,

    // Actions
    initialize: store.initialize,
    signUp: (email: string, password: string, username?: string) => 
      store.signUp({ email, password, username }),
    signIn: (email: string, password: string) => 
      store.signIn({ email, password }),
    completeMfaLogin: store.completeMfaLogin,
    requestMfaCodeAgain: store.requestMfaCodeAgain,
    clearPendingMfa: store.clearPendingMfa,
    signOut: store.signOut,
    signInWithGoogle: store.signInWithGoogle,
    resetPassword: store.resetPassword,
    resendVerificationEmail: store.resendVerificationEmail,
    fetchUserProfile: store.fetchUserProfile,
    updateUser: store.updateUser,
    clearError: store.clearError,
  };
};

